/**
 * MobileOptimizer token contract (DD-MOBILE-OPTIMIZER-0724 Phase 4/5).
 *
 * The single place the optimizer frames read design values from. EVERY color, space,
 * radius, and type value in the optimizer UI comes from here, which comes from
 * `dynastyTokens` — no hex literals, no one-off values (Phase 5 hard rule). Phase 6's
 * behavioral tests assert that rendered computed styles resolve to these token values, so
 * this module is also the contract those tests pin.
 */
import { dynastyTokens, withAlpha } from '../../../../services/colorService';

export const C = dynastyTokens.colors;      // primary (themeable), neutral ramp, semantic + risk aliases
export const SP = dynastyTokens.spacing;    // xs 8 / sm 12 / md 16 / lg 24
export const FS = dynastyTokens.fontSize;   // 2xs 10 / xs 12 / sm 14 / base 16 / lg 18 / xl 20 / 2xl 24
export const R = dynastyTokens.radius;      // none 0 (used everywhere) / sm 2 / base 4
export { withAlpha };

// House border tokens (locked mobile border — matches the design board's --border).
export const BORDER = `1px solid ${withAlpha(C.neutral[700], 0.5)}`;
export const BORDER_SOFT = `1px solid ${withAlpha(C.neutral[700], 0.26)}`;
export const PRIMARY = 'var(--team-primary, #eab308)';   // themeable per §token map; never hardcoded elsewhere

// Gap / rank tiers → the semantic tier aliases (top-3 / mid / bottom).
export function tierColor(status) {
  if (status === 'priority' || status === 'critical') return C.tierLow;     // = error
  if (status === 'improvable' || status === 'watch') return C.tierMid;      // = warning
  return C.tierElite;                                                       // = success (stable/dominant)
}

// The four engine risk profiles → the semantic risk aliases.
export function riskColor(profile) {
  switch (profile) {
    case 'safe_bet': return C.riskSafe;         // = success
    case 'high_upside': return C.riskUpside;    // = dataPurple
    case 'category_punt': return C.riskPunt;    // = warning
    case 'dynasty_play': return C.riskDynasty;  // = info
    default: return C.neutral[400];
  }
}

// Left-rule editorial section header (3px accent bar + 12px/600 uppercase label, 9px pad).
export function sectionHeaderStyle(color = PRIMARY) {
  return {
    bar: { width: 3, height: 14, borderRadius: R.none, background: color, flexShrink: 0 },
    row: { display: 'flex', alignItems: 'center', gap: SP.xs, padding: `${SP.sm} 0` },
    label: {
      fontSize: FS.xs, fontWeight: 600, letterSpacing: '1px',
      textTransform: 'uppercase', color, lineHeight: 1,
    },
  };
}

// Flat squared pill (no rounded containers — radius.none).
export function pillStyle(color, bgAlpha = 0.15) {
  return {
    fontSize: FS['2xs'], fontWeight: 800, letterSpacing: '.4px', textTransform: 'uppercase',
    padding: `2px 6px`, borderRadius: R.none, whiteSpace: 'nowrap',
    background: withAlpha(color, bgAlpha), color,
  };
}

// Numeric text — tabular-nums on ALL numeric data (hard rule).
export const NUM = { fontVariantNumeric: 'tabular-nums' };

// Hidden-scrollbar scroll container props (hard rule: no scrollbars).
export const NOBAR = { overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' };
